/**
 * Shared parts of the report screens (ADR 0018): filters kept in the URL (so KPI tiles can link to a
 * pre-filtered report), the export bar and small display helpers.
 */
import { Loading as SkeletonLoading, type LoadingShape } from "../../components/ui";
import {
  ALERT_TIME_ZONE,
  EVENT_GROUPS,
  REPORT_LIMITS,
  USAGE_BASES,
  type UsageBasis,
  buildSiteTree,
  checkReportRange,
  localParts,
  type EventGroup,
  type ExportFormat,
  type ReportKind,
  type SiteNode,
} from "@fts/core";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DateField } from "../../components/DateField";
import { formatDateInput } from "../../lib/date-input";
import { Icon } from "../../components/Icon";
import { useI18n, type MessageKey, type TFn } from "../../i18n";
import { errorKey } from "../../lib/errors";
import { descendantIds } from "../../lib/fleet";
import { exportFileName, isSystemText, systemText, type Cell, type ExportJob, type ExportMeta, type FormExport, type Sheet, type SummaryLine } from "../../lib/export/sheet";
import { maskImeisInText } from "@fts/core";
import { ExportTooLargeError, runExport } from "../../lib/export/runner";
import { navigate, type Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import "../../styles/reports.css";
import { basisOptionLabel, perSiteText, type WorkHoursInfo } from "./work-hours";

export const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
export const todayLocal = (now = Date.now()) => localParts(new Date(now), ALERT_TIME_ZONE).date;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export type ReportFilters = {
  siteId: string | null;
  vehicleIds: string[];
  operatorId: string | null;
  from: string;
  to: string;
  groups: EventGroup[];
  /** Usage report: utilisation basis (default "auto": shifts where the site has any, else working hours). */
  basis: UsageBasis;
};

/** Filters from the URL (unknown ids are dropped; default range: the last 7 local days). */
export function parseFilters(route: Route, data: TenantData, now = Date.now()): ReportFilters {
  const q = route.query;
  const today = todayLocal(now);
  const site = q.get("site");
  const vs = (q.get("v") ?? "").split(",").filter((id) => data.vehicles.some((v) => v.id === id));
  const op = q.get("op");
  const from = q.get("from");
  const to = q.get("to");
  const groups = (q.get("g") ?? "").split(",").filter((g): g is EventGroup => (EVENT_GROUPS as readonly string[]).includes(g));
  const b = q.get("b");
  return {
    siteId: site && data.sites.some((s) => s.id === site) ? site : null,
    vehicleIds: vs,
    operatorId: op && data.operators.operators.some((o) => o.id === op) ? op : null,
    from: from && DAY_RE.test(from) ? from : addDays(today, -6),
    to: to && DAY_RE.test(to) ? to : today,
    groups,
    basis: (USAGE_BASES as readonly string[]).includes(b ?? "") ? (b as UsageBasis) : "auto",
  };
}

export function filtersQuery(f: Partial<ReportFilters>): Record<string, string | undefined> {
  return {
    site: f.siteId || undefined,
    v: f.vehicleIds?.length ? f.vehicleIds.join(",") : undefined,
    op: f.operatorId || undefined,
    from: f.from,
    to: f.to,
    g: f.groups?.length ? f.groups.join(",") : undefined,
    b: f.basis && f.basis !== "auto" ? f.basis : undefined,
  };
}

export function siteOptions(tree: SiteNode[], depth = 0, out: Array<{ id: string; label: string }> = []) {
  for (const n of tree) {
    out.push({ id: n.id, label: `${" ".repeat(depth)}${n.name}` });
    siteOptions(n.children, depth + 1, out);
  }
  return out;
}

export const rangeKey = (issue: ReturnType<typeof checkReportRange>): MessageKey | null =>
  issue === "too_long" ? "reports.range.tooLong" : issue === "reversed" ? "reports.range.reversed" : issue === "invalid" ? "reports.range.invalid" : null;

/** Vehicle multi-select as a checkbox list in a disclosure (works with keyboard and on phones). */
function VehiclePicker({ data, siteId, value, onChange, single }: { data: TenantData; siteId: string | null; value: string[]; onChange: (ids: string[]) => void; single?: boolean }) {
  const { t } = useI18n();
  const inScope = useMemo(() => {
    if (!siteId) return data.vehicles;
    const ids = new Set<string>([siteId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of data.sites) if (s.parentId && ids.has(s.parentId) && !ids.has(s.id)) (ids.add(s.id), (grew = true));
    }
    return data.vehicles.filter((v) => ids.has(v.siteId));
  }, [data, siteId]);
  const sorted = [...inScope].sort((a, b) => a.fleetNo.localeCompare(b.fleetNo));
  if (single) {
    return (
      <div className="field field-inline">
        <label htmlFor="rf-vehicle">{t("reports.filter.vehicle")}</label>
        <select id="rf-vehicle" value={value[0] ?? ""} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}>
          <option value="">{t("reports.filter.chooseVehicle")}</option>
          {sorted.map((v) => (
            <option key={v.id} value={v.id}>
              {v.fleetNo}
            </option>
          ))}
        </select>
      </div>
    );
  }
  const label = value.length === 0 ? t("reports.filter.allVehicles") : t("reports.filter.nVehicles", { count: value.length });
  return (
    <div className="field field-inline">
      <span className="field-label" id="rf-vehicles-label">
        {t("reports.filter.vehicles")}
      </span>
      <details className="picker">
        <summary aria-labelledby="rf-vehicles-label rf-vehicles-value">
          <span id="rf-vehicles-value">{label}</span>
          <Icon name="chevronDown" size={16} />
        </summary>
        <div className="picker-list" role="group" aria-labelledby="rf-vehicles-label">
          {value.length > 0 && (
            <button type="button" className="link-button" onClick={() => onChange([])}>
              {t("reports.filter.clearVehicles")}
            </button>
          )}
          {sorted.map((v) => (
            <label key={v.id} className="check-row">
              <input
                type="checkbox"
                checked={value.includes(v.id)}
                onChange={(e) => onChange(e.target.checked ? [...value, v.id] : value.filter((x) => x !== v.id))}
              />
              <span className="mono">{v.fleetNo}</span>
            </label>
          ))}
        </div>
      </details>
    </div>
  );
}

/**
 * Filter bar of the date-range reports. Changes go to the URL; the screen re-fetches. `operator`: show the
 * operator filter (only names the user may see are listed: operators RLS).
 */
export function FilterBar({
  data,
  path,
  filters,
  operator = true,
  vehicles = "multi",
  groups = false,
  dates = true,
  basis = false,
  workHours,
  groupList = EVENT_GROUPS,
  keep,
  children,
}: {
  data: TenantData;
  path: string;
  filters: ReportFilters;
  operator?: boolean;
  vehicles?: "multi" | "single" | false;
  groups?: boolean;
  dates?: boolean;
  basis?: boolean;
  /** Usage report: effective working hours of the sites in scope (label of the "work" basis). */
  workHours?: WorkHoursInfo | null;
  /** Event groups offered as chips (default: all). */
  groupList?: readonly EventGroup[];
  /** Screen-specific query parameters kept when a filter changes (e.g. the heatmap mode). */
  keep?: Record<string, string | undefined>;
  children?: ReactNode;
}) {
  const { t, lang } = useI18n();
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);
  const ops = data.operators.operators.filter((o) => !o.anonymized).sort((a, b) => a.fullName.localeCompare(b.fullName, "tr"));
  const update = (patch: Partial<ReportFilters>) => navigate(path, { ...filtersQuery({ ...filters, ...patch }), ...keep });
  // Phones: the filters fold into a panel above the report (ADR 0030); wider screens always show them.
  const [open, setOpen] = useState(false);
  const site = filters.siteId ? data.sites.find((s) => s.id === filters.siteId)?.name : null;
  const brief = [dates ? `${formatDateInput(filters.from, lang)} – ${formatDateInput(filters.to, lang)}` : null, site ?? t("reports.filter.allSites")].filter(Boolean).join(" · ");
  return (
    <>
    <button type="button" className="filters-toggle" aria-expanded={open} aria-controls="report-filters" onClick={() => setOpen((x) => !x)}>
      <Icon name="filter" size={16} />
      <span className="filters-toggle-text">
        <span className="strong">{t("reports.filter.toggle")}</span>
        <span className="filters-toggle-brief">{brief}</span>
      </span>
      <Icon name="chevronDown" size={16} className="filters-toggle-chevron" />
    </button>
    <div id="report-filters" className={`filters report-filters${open ? " is-open" : ""}`} role="group" aria-label={t("reports.filter.label")}>
      <div className="field field-inline">
        <label htmlFor="rf-site">{t("reports.filter.site")}</label>
        <select id="rf-site" value={filters.siteId ?? ""} onChange={(e) => update({ siteId: e.target.value || null, vehicleIds: [] })}>
          <option value="">{t("reports.filter.allSites")}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {vehicles && <VehiclePicker data={data} siteId={filters.siteId} value={filters.vehicleIds} single={vehicles === "single"} onChange={(ids) => update({ vehicleIds: ids })} />}
      {operator && (
        <div className="field field-inline">
          <label htmlFor="rf-op">{t("reports.filter.operator")}</label>
          <select id="rf-op" value={filters.operatorId ?? ""} onChange={(e) => update({ operatorId: e.target.value || null })}>
            <option value="">{t("reports.filter.allOperators")}</option>
            {ops.map((o) => (
              <option key={o.id} value={o.id}>
                {o.fullName}
              </option>
            ))}
          </select>
        </div>
      )}
      {dates && (
        <>
          <div className="field field-inline">
            <label htmlFor="rf-from">{t("reports.filter.from")}</label>
            <DateField id="rf-from" value={filters.from} max={filters.to} required onChange={(v) => update({ from: v })} />
          </div>
          <div className="field field-inline">
            <label htmlFor="rf-to">{t("reports.filter.to")}</label>
            <DateField id="rf-to" value={filters.to} min={filters.from} required onChange={(v) => update({ to: v })} />
          </div>
        </>
      )}
      {basis && (
        <div className="field field-inline">
          <label htmlFor="rf-basis">{t("reports.filter.basis")}</label>
          <select id="rf-basis" value={filters.basis} onChange={(e) => update({ basis: e.target.value as UsageBasis })}
                  title={perSiteText(workHours) ? `${t("reports.filter.workHours")}: ${perSiteText(workHours)}` : undefined}>
            {USAGE_BASES.map((x) => (
              <option key={x} value={x} title={x === "work" ? (perSiteText(workHours) ?? undefined) : undefined}>
                {basisOptionLabel(t, x, workHours)}
              </option>
            ))}
          </select>
        </div>
      )}
      {groups && (
        <fieldset className="chip-group">
          <legend>{t("reports.filter.types")}</legend>
          {groupList.map((g) => (
            <label key={g} className={`chip${filters.groups.includes(g) ? " chip-on" : ""}`}>
              <input
                type="checkbox"
                checked={filters.groups.includes(g)}
                onChange={(e) => update({ groups: e.target.checked ? [...filters.groups, g] : filters.groups.filter((x) => x !== g) })}
              />
              {t(`reports.group.${g}`)}
            </label>
          ))}
        </fieldset>
      )}
      {children}
    </div>
    </>
  );
}

export type LoadState<T> = {
  status: "idle" | "loading" | "ready" | "error";
  data: T | null;
  error: string | null;
  /**
   * The request in flight or last settled (null when idle); resolves to null when it failed or was
   * superseded. Lets an export wait for a result the screen is still loading (UI phase D).
   */
  promise: Promise<T | null> | null;
};

/** Fetch on dependency change; stale responses are ignored (sequence guard). */
export function useLoad<T>(load: (() => Promise<T>) | null, deps: unknown[]): LoadState<T> {
  const { t } = useI18n();
  const [state, setState] = useState<Omit<LoadState<T>, "promise">>({ status: "idle", data: null, error: null });
  const seq = useRef(0);
  const promise = useRef<Promise<T | null> | null>(null);
  useEffect(() => {
    const mine = ++seq.current;
    if (!load) {
      promise.current = null;
      setState({ status: "idle", data: null, error: null });
      return;
    }
    setState((s) => ({ status: "loading", data: s.data, error: null }));
    const p = load();
    promise.current = p.then(
      (data) => (mine === seq.current ? data : null),
      () => null,
    );
    p.then((data) => mine === seq.current && setState({ status: "ready", data, error: null })).catch(
      (e: unknown) => mine === seq.current && setState({ status: "error", data: null, error: t(errorKey(e)) }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { ...state, promise: promise.current };
}

/** A ref that always holds the latest value (read inside callbacks created on an earlier render). */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/** A promise's value, or null when it fails or takes longer than `ms`. */
export function settleWithin<T>(p: Promise<T | null> | null | undefined, ms = 3000): Promise<T | null> {
  if (!p) return Promise.resolve(null);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((r) => (timer = setTimeout(() => r(null), ms)));
  return Promise.race([p.catch(() => null), late]).finally(() => clearTimeout(timer));
}

/** Time the PDF waits for the previous period's result still loading (then: no comparison). */
export const PREVIOUS_WAIT_MS = 3000;

export function Loading({ shape = "table" }: { shape?: LoadingShape }) {
  return <SkeletonLoading shape={shape} />;
}

/** Translated fixed labels of the export files. */
export function exportLabels(t: TFn): ExportMeta["labels"] {
  return {
    filters: t("reports.export.filters"),
    generatedAt: t("reports.export.generatedAt"),
    generatedBy: t("reports.export.generatedBy"),
    timeZone: t("reports.export.timeZone"),
    page: t("reports.export.page"),
    yes: t("common.yes"),
    no: t("common.no"),
    info: t("reports.export.info"),
    pdfRowCap: t("reports.export.pdfRowCap"),
    noRows: t("reports.export.noRows"),
    mapUnavailable: t("reports.export.mapUnavailable"),
    footer: t("reports.export.footer"),
    period: t("reports.period"),
    dates: t("reports.filter.dates"),
    chartCap: t("reports.export.chartCap"),
    summaryCapped: t("reports.export.summaryCapped"),
    summary: t("reports.summary"),
    sample: t("reports.export.sample"),
    product: t("app.productLabel"),
  };
}

/** Every text cell of the sheets with Teltonika device ids (TLT-<IMEI>) masked (ADR 0034). */
export function maskSheets(sheets: Sheet[]): Sheet[] {
  const cell = (c: Cell): Cell => (typeof c === "string" ? maskImeisInText(c) : isSystemText(c) ? systemText(maskImeisInText(c.system)) : c);
  return sheets.map((sh) => ({ ...sh, rows: sh.rows.map((r) => r.map(cell)) }));
}

export type ExportSpec = {
  report: ReportKind;
  reportName: string;
  slug: string;
  part: string;
  tz: string;
  filters: [string, string][];
  /** Rows on screen (the 50 000-row cap is checked before asking the server). */
  rows: number;
  /**
   * Fetch the data for the file through the provider's export call (the server logs who, report, filters
   * and the row count it counted) and turn it into sheets. Files are built only from this data.
   */
  load: (format: ExportFormat) => Promise<{ sheets: Sheet[]; tz?: string; summary?: SummaryLine[]; chart?: ExportMeta["chart"]; form?: FormExport }>;
  /** PDF: the period as text (UI phase D). */
  period?: string;
  /** PDF only: map snapshot (data URL); null = the map could not be captured. */
  mapImage?: () => string | null;
  /** PDF only: what to say when the map could not be captured (default: the route report's text). */
  mapUnavailable?: string;
};

/**
 * CSV / CSV (;) / XLSX / PDF buttons. Every export is logged first (who, report, filters, rows; KVKK);
 * the file is built in a Web Worker and saved by the browser. Above 50 000 rows nothing is built.
 */
export type ExportButton = "csv" | "csvSemicolon" | "xlsx" | "pdf";
const ALL_BUTTONS: ExportButton[] = ["csv", "csvSemicolon", "xlsx", "pdf"];

export function ExportBar({ spec, disabled, buttons = ALL_BUTTONS, pdfLabel }: { spec: ExportSpec | null; disabled?: boolean; buttons?: ExportButton[]; pdfLabel?: string }) {
  const { t, lang } = useI18n();
  const { provider } = useSession();
  const roleLabel = useRoleLabel();
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "info"; text: string } | null>(null);
  const tooMany = !!spec && spec.rows > REPORT_LIMITS.exportMaxRows;

  const go = async (format: ExportFormat, separator?: "," | ";") => {
    if (!spec) return;
    setBusy(format);
    setMessage(null);
    try {
      const { sheets, tz, summary, chart, form } = await spec.load(format);
      const meta: ExportMeta = {
        report: spec.report,
        reportName: spec.reportName,
        locale: lang,
        tz: tz ?? spec.tz,
        filters: spec.filters,
        generatedAt: new Date().toISOString(),
        // Every format: the role only, never the person's name (owner decision 2026-11-03, ADR 0031
        // annex; KVKK). The server-side audit row in report_exports records who exported.
        generatedBy: roleLabel,
        generatedByRole: roleLabel,
        period: spec.period,
        summary,
        chart,
        sample: provider.mode === "demo",
        form,
        labels: { ...exportLabels(t), ...(spec.mapUnavailable ? { mapUnavailable: spec.mapUnavailable } : {}) },
      };
      // Files leave the system: a Teltonika device id (TLT-<IMEI>) is masked to its last 4 digits (ADR 0034).
      const masked = maskSheets(sheets);
      const job: ExportJob = { format, meta: { ...meta, filters: meta.filters.map(([k, v]) => [k, maskImeisInText(v)] as [string, string]) }, sheets: masked, separator, mapImage: format === "pdf" && spec.mapImage ? spec.mapImage() : undefined };
      await runExport(job, exportFileName(spec.slug, spec.part, format));
      setMessage({ tone: "info", text: t("reports.export.done", { rows: masked[0]?.rows.length ?? 0 }) });
    } catch (e) {
      setMessage({ tone: "error", text: e instanceof ExportTooLargeError ? t("reports.export.tooMany", { max: REPORT_LIMITS.exportMaxRows }) : t(errorKey(e)) });
    } finally {
      setBusy(null);
    }
  };

  const off = disabled || !spec || busy !== null || tooMany;
  return (
    <div className="export-bar">
      <div className="export-buttons" role="group" aria-label={t("reports.export.label")}>
        {buttons.includes("csv") && (
          <button type="button" className="button button-secondary" disabled={off} onClick={() => go("csv", ",")}>
            <Icon name="download" size={16} /> {busy === "csv" ? t("reports.export.working") : t("reports.export.csv")}
          </button>
        )}
        {buttons.includes("csvSemicolon") && (
          <button type="button" className="button button-secondary" disabled={off} onClick={() => go("csv", ";")} title={t("reports.export.csvSemicolonHint")}>
            {t("reports.export.csvSemicolon")}
          </button>
        )}
        {buttons.includes("xlsx") && (
          <button type="button" className="button button-secondary" disabled={off} onClick={() => go("xlsx")}>
            {!buttons.includes("csv") && <Icon name="download" size={16} />} {busy === "xlsx" ? t("reports.export.working") : t("reports.export.xlsx")}
          </button>
        )}
        {buttons.includes("pdf") && (
          <button type="button" className="button button-secondary" disabled={off} onClick={() => go("pdf")}>
            {buttons.length === 1 && <Icon name="download" size={16} />} {busy === "pdf" ? t("reports.export.working") : (pdfLabel ?? t("reports.export.pdf"))}
          </button>
        )}
      </div>
      <p className="export-note">
        {tooMany ? <span className="form-error">{t("reports.export.tooMany", { max: REPORT_LIMITS.exportMaxRows })}</span> : t("reports.export.audit")}
      </p>
      {message && (
        <p className={message.tone === "error" ? "form-error" : "export-done"} role={message.tone === "error" ? "alert" : "status"}>
          {message.text}
        </p>
      )}
    </div>
  );
}

/** "Örnek veri" marker for demo mode and the time zone the days were cut in. */
export function ReportMeta({ tz, sample }: { tz: string | null; sample: boolean }) {
  const { t } = useI18n();
  return (
    <p className="report-meta">
      {tz && <span>{t("reports.tz", { tz })}</span>}
      {sample && <span className="badge tone-warn">{t("reports.sample")}</span>}
    </p>
  );
}

/** The role of the person exporting, for the PDF's "generated by" (no name on paper; the audit log keeps who). */
export function useRoleLabel(): string {
  const { t } = useI18n();
  const { role, user } = useSession();
  return user?.platformAdmin ? t("tenant.platformAdmin") : role ? t(`role.${role}`) : "—";
}

/** Vehicles of a report's scope: the site subtree (or every site) and the optional selection. */
export function scopeVehicles(data: TenantData, siteId: string | null, vehicleIds: string[]): TenantData["vehicles"] {
  const inSite = siteId ? descendantIds(data.sites, siteId) : null;
  return data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (vehicleIds.length === 0 || vehicleIds.includes(v.id)));
}

/** The selected site's name, or "Tüm sahalar". */
export function siteLabel(t: TFn, data: TenantData, siteId: string | null): string {
  return siteId ? (data.sites.find((s) => s.id === siteId)?.name ?? "—") : t("reports.filter.allSites");
}

/** The filters as chips beside the title (everything of filterSummary except the dates, shown as the period). */
export function contextChips(t: TFn, summary: [string, string][]): [string, string][] {
  return summary.filter(([k]) => k !== t("reports.filter.dates"));
}

/** Filter summary for files: site, vehicles, operator, dates. */
export function filterSummary(t: TFn, data: TenantData, f: ReportFilters, opts: { dates?: string; operator?: boolean; groups?: boolean; basis?: boolean; workHours?: WorkHoursInfo | null } = {}): [string, string][] {
  const out: [string, string][] = [];
  out.push([t("reports.filter.site"), f.siteId ? (data.sites.find((s) => s.id === f.siteId)?.name ?? "—") : t("reports.filter.allSites")]);
  out.push([
    t("reports.filter.vehicles"),
    f.vehicleIds.length ? f.vehicleIds.map((id) => data.vehicles.find((v) => v.id === id)?.fleetNo ?? "—").join(", ") : t("reports.filter.allVehicles"),
  ]);
  if (opts.operator !== false && f.operatorId) out.push([t("reports.filter.operator"), data.operators.operators.find((o) => o.id === f.operatorId)?.fullName ?? "—"]);
  if (opts.groups && f.groups.length) out.push([t("reports.filter.types"), f.groups.map((g) => t(`reports.group.${g}`)).join(", ")]);
  if (opts.basis) {
    out.push([t("reports.filter.basis"), basisOptionLabel(t, f.basis, opts.workHours)]);
    const per = perSiteText(opts.workHours);
    if (per && (f.basis === "work" || f.basis === "auto")) out.push([t("reports.filter.workHours"), per]);
  }
  out.push([t("reports.filter.dates"), opts.dates ?? `${f.from} – ${f.to}`]);
  return out;
}
